import api from "../../../utils/api";

// ===========================================================================
// Cargo Market (bolsa de carga) — cliente tipado de /cargoExchange/*
// ---------------------------------------------------------------------------
// El backend (torre-de-control-api · services/cargoExchangeService) resuelve la
// compañía del usuario, el nivel de confianza a partir del expediente y de la
// flotilla real, y crea el viaje en Administración de Flotilla al confirmar.
// ===========================================================================

export type TrustLevel = "registrado" | "verificado" | "rastreado" | "premium";
export type UnitType =
  | "caja_53"
  | "sencillo"
  | "full"
  | "thermo"
  | "plataforma"
  | "pipa"
  | "tolva"
  | "torton"
  | "rabon"
  | "camioneta_35";
export type PricingMode = "fixed" | "offers" | "suggested";
export type OwnerType = "company" | "vehicle" | "trailer" | "driver" | "shipper";
export type DocStatus = "pending" | "approved" | "rejected";
export type TripStatus =
  | "awarded"
  | "confirmed"
  | "to_pickup"
  | "at_pickup"
  | "loaded"
  | "in_transit"
  | "at_dropoff"
  | "delivered"
  | "closed"
  | "cancelled";
export type CheckIn = "geofence" | "qr" | "pin";

/** Firestore Timestamp serializado o ISO. */
export type When = string | { _seconds?: number; seconds?: number } | null | undefined;

export interface Place {
  key: string;
  city: string;
  state: string;
  lat: number;
  lng: number;
  address?: string | null;
  geofenceId?: string | null;
}

export interface Compliance {
  ok: boolean;
  missing: string[];
  pending: string[];
  expiring: { docType: string; label: string; daysLeft: number; expiresAt: string }[];
  badges: string[];
}

export interface CarrierTrust {
  level: TrustLevel;
  canOperate: boolean;
  company: Compliance;
  units: (Compliance & { id: string; name: string; plate: string | null; hasGps: boolean })[];
  trailers: (Compliance & { id: string; name: string; plate: string | null })[];
  drivers: (Compliance & { id: string; name: string })[];
  nextSteps: string[];
  expiring: { docType: string; label: string; daysLeft: number; ownerType: string; ownerId: string | null; ownerName: string }[];
  stats: { closedTrips: number; rating: number };
  unitTypes: Record<string, UnitType>;
  gpsByVehicle: Record<string, string>;
}

export interface Profile {
  company_id: string;
  legalName: string;
  tradeName: string | null;
  rfc: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  roles: { carrier: boolean; shipper: boolean };
  shipperKind: "shipper" | "forwarder";
  credit: { requested: boolean; enabled: boolean; days: number | null };
  frequentGeofenceIds: string[];
  lanes: { origin: Place; destination: Place }[];
  assets: Record<string, { unitType: UnitType | null; economicNumber: string | null }>;
  stats: { closedTrips: number; ratingSum: number; ratingCount: number };
  trustLevel: TrustLevel;
  shipperLevel: TrustLevel;
  sandboxOf: string | null;
  /** Ficha de ejemplo del modo Sandbox (se retira al apagarlo). */
  demoSeed?: boolean;
}

export interface KycDocument {
  id: string;
  company_id: string;
  ownerType: OwnerType;
  ownerId: string;
  docType: string;
  label: string;
  number: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  fileUrl: string | null;
  status: DocStatus;
  reviewNote: string | null;
  companyName?: string;
  /** Unidad, remolque u operador (cola de Administración). */
  ownerName?: string;
  /** Documento de ejemplo del Sandbox de la compañía activa (cola de Administración). */
  sample?: boolean;
  createdAt?: When;
}

export interface UnitTypeSpec {
  label: string;
  tons: number;
  m3: number | null;
  ratePerKm: number;
  hazmat?: boolean;
}
export interface DocTypeSpec {
  label: string;
  required: boolean;
  validDays?: number;
  badge?: boolean;
  hazmat?: boolean;
}

export interface Me {
  companyId: string;
  profile: Profile | null;
  carrier: CarrierTrust;
  shipper: Compliance & { level: TrustLevel; canOperate: boolean };
  documents: KycDocument[];
  fleet: {
    vehicles: { id: string; name: string | null; plate: string | null; brand: string | null; model: string | null; type: string | null; remolque_id: string | null; unitType: UnitType; deviceId: string | null }[];
    trailers: { id: string; name: string | null; plate: string | null; type: string | null; vehicle_id: string | null }[];
    drivers: { id: string; name: string; licenseId: string | null; licenseExp: string | null; vehicle_id: string | null }[];
    geofences: { id: string; name: string; address: string | null; type: string | null }[];
  };
  catalogs: {
    unitTypes: Record<UnitType, UnitTypeSpec>;
    docTypes: Record<OwnerType, Record<string, DocTypeSpec>>;
    trustLevels: TrustLevel[];
    tripFlow: TripStatus[];
    tripLabels: Record<TripStatus, string>;
    commissionPct: number;
  };
  unreadNotifications: number;
}

export interface Load {
  id: string;
  folio: string;
  title: string;
  company_id: string;
  shipperName: string;
  shipperKind: "shipper" | "forwarder";
  shipperLevel: TrustLevel;
  shipperContact?: { name: string | null; phone: string | null; email: string | null } | null;
  origin: Place;
  destination: Place;
  pickupWindow: { from: string; to: string };
  deliveryWindow: { from: string; to: string } | null;
  merchandise: string;
  satKey: string | null;
  weightTons: number;
  volumeM3: number | null;
  declaredValue: number | null;
  unitType: UnitType;
  hazmat: boolean;
  maneuvers: string | null;
  requirements: { custody: boolean; gps: boolean; oea: boolean };
  trustRequired: TrustLevel;
  pricingMode: PricingMode;
  /** Total que paga el embarcador (comisión incluida). */
  price: number | null;
  suggested?: { min: number; max: number; km: number };
  offersCloseAt: string | null;
  status: "open" | "awarded" | "cancelled" | "expired";
  offersCount: number;
  bestOfferTotal: number | null;
  invitedCarrierIds?: string[];
  awardedCarrierCompanyId: string | null;
  tripId: string | null;
  notes: string | null;
  sandboxOf: string | null;
  /**
   * Carga de demostración de la plataforma (CC-CONSOLE#1639): la vitrina
   * pública nunca queda vacía. Tomarla u ofertar no crea viaje: el API
   * registra el interés y responde `{ showcase: true, message }`.
   */
  showcase?: boolean;
  createdAt?: When;
  // Vista del transportista
  freightOffered?: number | null;
  invited?: boolean;
  eligible?: boolean;
  myOffer?: Offer | null;
  bestMatch?: { capacityId: string; score: number; isBackhaul: boolean } | null;
}

export interface Pricing {
  freight: number;
  commissionPct: number;
  commission: number;
  total: number;
  currency: string;
  detention?: number;
}

export interface Offer {
  id: string;
  loadId: string;
  company_id: string;
  carrierName: string;
  carrierLevel: TrustLevel;
  freight: number;
  total: number;
  pricing: Pricing;
  note: string | null;
  status: "active" | "accepted" | "rejected" | "withdrawn";
  carrierRating?: number;
  carrierTrips?: number;
  createdAt?: When;
}

export interface Capacity {
  id: string;
  company_id: string;
  carrierName: string;
  carrierLevel: TrustLevel;
  origin: Place;
  destination: Place | null;
  availableAt: string;
  unitType: UnitType;
  capacityTons: number;
  capacityM3: number | null;
  rate: number | null;
  isBackhaul: boolean;
  vehicleId: string;
  remolqueId: string | null;
  unitName: string | null;
  unitPlate: string | null;
  hasGps: boolean;
  status: "open" | "cancelled" | "booked" | "expired";
  notes: string | null;
  sandboxOf?: string | null;
}

export interface Match {
  score: number;
  capacity: Capacity;
  carrier: { companyId: string; name: string; level: TrustLevel; rating: number; trips: number };
}

export interface TripEvent {
  status: TripStatus;
  at: string;
  by: "carrier" | "shipper" | "platform";
  note: string | null;
  evidence?: { checkIn: CheckIn | null; signedBy: string | null; sealNumber: string | null; sealIntact: boolean | null; photos: string[] };
}

export interface Trip {
  id: string;
  folio: string;
  loadId: string;
  role: "carrier" | "shipper";
  shipper_company_id: string;
  carrier_company_id: string;
  shipperName: string;
  carrierName: string;
  title: string;
  origin: Place;
  destination: Place;
  pickupWindow: { from: string; to: string };
  deliveryWindow: { from: string; to: string } | null;
  merchandise: string;
  satKey: string | null;
  weightTons: number;
  volumeM3: number | null;
  declaredValue: number | null;
  unitType: UnitType;
  hazmat: boolean;
  km: number;
  pricing: Pricing;
  payment: { mode: "prepaid" | "credit"; days?: number; status: string; provider: string };
  status: TripStatus;
  events: TripEvent[];
  vehicleId: string | null;
  remolqueId: string | null;
  driverId: string | null;
  unit: { vehicleId: string | null; name: string | null; plate: string | null; economicNumber: string | null; trailer: { id: string | null; name: string | null; plate: string | null } | null; hasGps: boolean } | null;
  driver: { driverId: string | null; name: string; licenseId: string | null; phone: string | null } | null;
  fleetTripId: string | null;
  deviceId: string | null;
  incidents: { type: string; note: string | null; at: string; by: string; status: string }[];
  ratings: Partial<Record<"carrier" | "shipper", { score: number; comment: string | null; at: string }>>;
  detention: { freeHours: number; ratePerHour: number; billableHours: number; amount: number; pickup: { hours: number }; dropoff: { hours: number } } | null;
  cartaPorte: { status: "pending" | "generated"; folio?: string };
  shipperContact: { name: string | null; phone: string | null; email: string | null } | null;
  carrierContact: { name: string | null; phone: string | null; email: string | null } | null;
  contactsRevealed: boolean;
  sandboxOf: string | null;
}

export interface CarrierCard {
  companyId: string;
  name: string;
  level: TrustLevel;
  rating: number;
  ratingCount: number;
  closedTrips: number;
  lanes: string[];
  openCapacity: number;
  unitTypes: UnitType[];
}

export interface SuggestedRate {
  km: number;
  perKm: number;
  min: number;
  suggested: number;
  max: number;
  totalMin: number;
  totalSuggested: number;
  totalMax: number;
  commissionPct: number;
  samples: "history" | "model";
}

export interface ExchangeNotification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  ref: { loadId?: string; tripId?: string; documentId?: string; capacityId?: string } | null;
  /** Papel de la compañía en el aviso: decide a qué pantalla lleva. */
  role?: "carrier" | "shipper" | "admin" | null;
  read: boolean;
  createdAt?: When;
}

export interface CommissionRow {
  id: string;
  folio: string;
  shipperName: string;
  carrierName: string;
  closedAt: string;
  freight: number;
  commission: number;
  total: number;
  payment: string | null;
}

export interface CommissionTotals {
  trips: number;
  freight: number;
  commission: number;
  total: number;
}

export interface Commissions {
  /** Sólo viajes REALES del mercado. */
  rows: CommissionRow[];
  totals: CommissionTotals;
  commissionPct: number;
  /** Ejemplos del Sandbox de la compañía activa, aparte (no suman a los totales reales). */
  sample?: { companyId: string; rows: CommissionRow[]; totals: CommissionTotals } | null;
}

// --- Alta de transportistas (Administración) ---------------------------------

/** Cómo va el alta: `queued` lo aprovisiona el trigger (lote); el resto es final. */
export type CarrierInviteStatus = "queued" | "provisioning" | "provisioned" | "partial" | "existing-account" | "failed";
/** Avance del transportista después del correo (sólo hacia adelante). */
export type CarrierInviteStage = "invited" | "opened" | "profile" | "verified" | "tracked";

/** Lo que se captura por transportista (formulario o fila del lote). */
export interface CarrierInviteInput {
  legalName: string;
  tradeName?: string | null;
  rfc?: string | null;
  contactName: string;
  contactEmail: string;
  contactPhone?: string | null;
  city?: string | null;
  state?: string | null;
  unitsCount?: number | null;
  notes?: string | null;
}

export interface CarrierInvite extends CarrierInviteInput {
  id: string;
  status: CarrierInviteStatus;
  progress: CarrierInviteStage;
  progressAt?: Partial<Record<CarrierInviteStage, string>>;
  refs: { companyId: string | null; userId: string | null; crmCompanyId: string | null; crmDealId: string | null; leadId: string | null };
  errors: { step: string; message: string }[];
  email: { sent: boolean; sentAt: string | null; sendCount: number; lastError: string | null };
  batchId: string | null;
  createdByName: string | null;
  createdAt: string;
}

export interface CarrierInviteList {
  rows: CarrierInvite[];
  summary: { total: number; byProgress: Record<CarrierInviteStage, number>; byStatus: Partial<Record<CarrierInviteStatus, number>> };
  stages: { id: CarrierInviteStage; label: string }[];
}

/** Resultado por fila: `invalid`/`duplicate`/`limit` no se crearon. */
export interface CarrierInviteResult {
  total: number;
  created: number;
  skipped: number;
  results: { row: number; email: string | null; status: CarrierInviteStatus | "invalid" | "duplicate" | "limit"; reason?: string; inviteId?: string }[];
}

/**
 * Respuesta de `loads/take` y `offers` sobre una carga de demostración
 * (CC-CONSOLE#1639): no hay viaje ni oferta, sólo el aviso para la persona.
 */
export interface ShowcaseResponse {
  showcase: true;
  kind?: "take" | "offer";
  interestId?: string;
  /** Ya había registrado interés en esta misma carga. */
  repeated?: boolean;
  messageKey?: string;
  /** Texto ya traducido por el API (según `?lang=`). */
  message: string;
  /** A dónde invita el API después («Publicar mi capacidad»). */
  next?: string;
}

/** Ruta interna de Cargo Market que propone el API, o null si no es segura. */
export const showcaseNextPath = (value: unknown): string | null =>
  typeof value === "string" && /^\/cargo-market\/[A-Za-z0-9/_-]*$/.test(value) ? value : null;

export const isShowcaseResponse = (value: unknown): value is ShowcaseResponse =>
  !!value && typeof value === "object" && (value as { showcase?: unknown }).showcase === true;

/** Código con el que el API rechaza (409) a un transportista sin el sello «Verificado». */
export const CARRIER_NOT_VERIFIED = "carrierNotVerified";

/** ¿El error es el 409 `carrierNotVerified` del API (el `message` viene traducido; la señal es el `code`)? */
export function isCarrierNotVerifiedError(err: unknown): boolean {
  const e = err as { response?: { status?: number; data?: { meta?: { code?: string }; code?: string } } } | null;
  const code = e?.response?.data?.meta?.code ?? e?.response?.data?.code;
  return code === CARRIER_NOT_VERIFIED;
}

const data = <T,>(p: Promise<{ data: { data: T } }>) => p.then((r) => r.data.data);

export const exchangeApi = {
  me: () => data<Me>(api.get("/cargoExchange/me")),
  saveProfile: (body: Partial<Profile> & { assets?: Record<string, { unitType?: UnitType; economicNumber?: string }> }) =>
    data<Profile>(api.put("/cargoExchange/profile", body)),
  submitDocument: (body: { ownerType: OwnerType; ownerId?: string; docType: string; number?: string; issuedAt?: string | null; expiresAt?: string | null; fileUrl?: string }) =>
    data<KycDocument>(api.post("/cargoExchange/documents", body)),
  suggestedRate: (params: { origin: string; destination: string; unitType: UnitType; backhaul?: boolean }) =>
    data<SuggestedRate>(api.get("/cargoExchange/suggestedRate", { params })),

  loads: (scope: "mine" | "board") => data<Load[]>(api.get("/cargoExchange/loads", { params: { scope } })),
  load: (id: string) =>
    data<{ load: Load; offers?: Offer[]; matches?: Match[]; myOffer?: Offer | null; role: "shipper" | "carrier" }>(
      api.get("/cargoExchange/load", { params: { id } }),
    ),
  createLoad: (body: Record<string, unknown>) => data<Load>(api.post("/cargoExchange/loads", body)),
  cancelLoad: (id: string, reason?: string) => data<Load>(api.post("/cargoExchange/loads/cancel", { id, reason })),
  takeLoad: (id: string) => data<Trip | ShowcaseResponse>(api.post("/cargoExchange/loads/take", { id })),
  invite: (loadId: string, carrierCompanyId: string) => data<unknown>(api.post("/cargoExchange/loads/invite", { loadId, carrierCompanyId })),

  placeOffer: (body: { loadId: string; freight: number; note?: string; capacityId?: string }) =>
    data<Offer | ShowcaseResponse>(api.post("/cargoExchange/offers", body)),
  acceptOffer: (id: string) => data<Trip>(api.post("/cargoExchange/offers/accept", { id })),
  withdrawOffer: (id: string) => data<Offer>(api.post("/cargoExchange/offers/withdraw", { id })),

  capacity: (scope: "mine" | "board") => data<Capacity[]>(api.get("/cargoExchange/capacity", { params: { scope } })),
  createCapacity: (body: Record<string, unknown>) => data<Capacity>(api.post("/cargoExchange/capacity", body)),
  cancelCapacity: (id: string) => data<Capacity>(api.post("/cargoExchange/capacity/cancel", { id })),

  trips: (role?: "carrier" | "shipper") => data<Trip[]>(api.get("/cargoExchange/trips", { params: { role } })),
  trip: (id: string) => data<Trip>(api.get("/cargoExchange/trip", { params: { id } })),
  assignTrip: (body: { tripId: string; vehicleId: string; remolqueId?: string | null; driverId: string }) =>
    data<Trip>(api.post("/cargoExchange/trips/assign", body)),
  advanceTrip: (body: { tripId: string; status: TripStatus; note?: string; evidence?: Record<string, unknown> }) =>
    data<Trip>(api.post("/cargoExchange/trips/advance", body)),
  incident: (body: { tripId: string; type: string; note?: string }) => data<Trip>(api.post("/cargoExchange/trips/incident", body)),
  rateTrip: (body: { tripId: string; score: number; comment?: string }) => data<Trip>(api.post("/cargoExchange/trips/rate", body)),

  carriers: () => data<CarrierCard[]>(api.get("/cargoExchange/carriers")),
  notifications: () => data<ExchangeNotification[]>(api.get("/cargoExchange/notifications")),
  readNotifications: () => data<unknown>(api.post("/cargoExchange/notifications/read", {})),

  adminDocuments: () => data<KycDocument[]>(api.get("/cargoExchange/admin/documents")),
  adminReview: (id: string, decision: "approved" | "rejected", note?: string) =>
    data<KycDocument>(api.post("/cargoExchange/admin/review", { id, decision, note })),
  adminCommissions: () => data<Commissions>(api.get("/cargoExchange/admin/commissions")),
  adminCarrierInvites: () => data<CarrierInviteList>(api.get("/cargoExchange/admin/carrierInvites")),
  adminCreateCarrierInvite: (body: CarrierInviteInput) => data<CarrierInviteResult>(api.post("/cargoExchange/admin/carrierInvites", body)),
  adminCreateCarrierInvitesBatch: (rows: CarrierInviteInput[]) => data<CarrierInviteResult>(api.post("/cargoExchange/admin/carrierInvites/batch", { rows })),
  adminResendCarrierInvite: (id: string) => data<CarrierInvite>(api.post("/cargoExchange/admin/carrierInvites/resend", { id })),
  adminRetryCarrierInvite: (id: string) => data<CarrierInvite>(api.post("/cargoExchange/admin/carrierInvites/retry", { id })),
};

/** Mensaje legible de un error de la API (el backend responde `{ message }`). */
export function apiErrorMessage(err: unknown, fallback = "No se pudo completar la operación"): string {
  const e = err as { response?: { data?: { message?: string; meta?: { message?: string } } }; message?: string };
  return e?.response?.data?.message || e?.response?.data?.meta?.message || e?.message || fallback;
}
